import { meGetVoteTokens } from '../features/queries/meGetVoteTokens';
import { useInfiniteQ } from '../lib/query';
import { QueryNameSpace } from '../lib/query/query';
import { __, dummyArray } from '../utils';
import TransactionsListItemSkeleton from './Gamification/TransactionsListItemSkeleton';
import VotingTokenTransactionDetail from './VotingTokenTransactionDetail';
import VotingTokenTransactionsListItem from './VotingTokenTransactionsListItem';
import {
  CircularProgress,
  List,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

export const VOTING_TRANSACTIONS_LIST_LIMIT = 15;

type Props = {
  sx?: React.ComponentProps<typeof List>['sx'];
};

const TokenTransactionsList: React.FC<Props> = ({ sx }) => {
  const [modalVoteToken, setModalVoteToken] = React.useState<{
    token_id: string;
    target: string;
    inserted_at?: string;
    expires_at?: string;
    revoked_at?: string;
  } | null>(null);

  const queryClient = useQueryClient();

  const loaderRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const {
    data: transactions,
    isLoading,
    hasNextPage,
    refetch,
    fetchNextPage,
  } = useInfiniteQ(
    meGetVoteTokens,
    { limit: VOTING_TRANSACTIONS_LIST_LIMIT },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (!lastPage || !lastPage.nextCursor) return undefined;
        return lastPage.nextCursor;
      },
    },
  );

  React.useEffect(() => {
    if (isLoading) return;
    const loader = loaderRef.current;
    if (!loader) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { root: listRef.current, threshold: 0.1 },
    );
    observer.observe(loader);

    return () => observer.disconnect();
  }, [fetchNextPage, isLoading]);

  const formattedTransactions = React.useMemo(() => {
    return Array.isArray(transactions?.pages)
      ? transactions.pages.flatMap((page) => page.tokens)
      : [];
  }, [transactions]);

  return (
    <Stack>
      <List
        ref={listRef}
        sx={{
          position: 'absolute',
          inset: 0,
          overflowY: 'auto',
          p: 0,
          '& > *:not(:last-child)': {
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
          ...sx,
        }}
      >
        {isLoading ? (
          dummyArray(VOTING_TRANSACTIONS_LIST_LIMIT).map((_, index) => (
            <TransactionsListItemSkeleton key={index} />
          ))
        ) : formattedTransactions.length > 0 ? (
          formattedTransactions.map((token) => {
            return (
              <VotingTokenTransactionsListItem
                key={token.token_id}
                transaction={token}
                onOpen={() => {
                  setModalVoteToken(token);
                }}
              />
            );
          })
        ) : (
          <Typography p={3} align='center' variant='body2'>
            {__('Žiadne záznamy.')}
          </Typography>
        )}
        {hasNextPage && (
          <Stack
            ref={loaderRef}
            sx={{
              minHeight: 40,
              height: 40,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={24} />
          </Stack>
        )}
      </List>
      <VotingTokenTransactionDetail
        open={!!modalVoteToken}
        onClose={() => setModalVoteToken(null)}
        voteToken={modalVoteToken}
        onSuccess={async () => {
          await refetch();
          // Aktualizuje pocet aktivnych/dostupnych zetonov v suhrne profilu
          await queryClient.refetchQueries({
            queryKey: [QueryNameSpace.meGetVoteTokensSummary],
          });
        }}
      />
    </Stack>
  );
};

export default TokenTransactionsList;
